import _ from 'lodash'

import Vue from 'vue'

import { mapGetters, mapActions } from 'vuex'

import { pWarn } from '_/helpers/logger'

import { router } from '_/router/router'

import { store } from '_/vuex/store'

import i18n from '_/i18n'

import { userHasRole } from '_/vuex/actions/userActions'

import * as adminSlots from '~/config/adminSlots'

import { storage } from '_/services/storage'

import { eventBus } from '_/helpers/eventBus'

import {
  bindValidatorToForm,
  destroyValidator,
  processErrors,
  resetValidator,
} from '_/services/formValidator'

const photonConfig = require('~/config/config.json')

const FormField = Vue.component(
  'FormField',
  require('_/components/UserInterface/EntryForm/FormField/FormField.vue')
    .default
)

export const PermissionNameGeneratorHandler = Vue.component(
  'PermissionNameGeneratorHandler',
  require('_/components/UserInterface/PermissionNameGeneratorHandler/PermissionNameGeneratorHandler.vue')
    .default
)

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    extendedFieldEditing: {
      default: false,
      type: Boolean,
    },
    fields: {
      type: Array,
    },
    formFieldsResetProp: {
      required: true,
      type: Object,
    },
    inSidebar: {
      default: false,
      type: Boolean,
    },
    shouldRouterPush: {
      default: true,
      type: Boolean,
    },
    vuexModule: {
      required: true,
      type: String,
    },
  },

  /**
   * Set the component data
   *
   * @return  {object}
   */
  data() {
    return {
      formFieldsReset: {
        includeFields: [],
        resetData: null,
      },
      serverError: null,
      isSecondFieldFocused: false,
    }
  },

  /**
   * Define components
   *
   * @type  {Object}
   */
  components: {
    ...adminSlots,
    FormField,
    PermissionNameGeneratorHandler,
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    ...mapActions('admin', ['setCreateEntryUI', 'toggleEntryUpdated']),

    ...mapActions('user', ['checkMe']),

    ...mapActions('notification', ['addNotification']),

    ...mapActions('photonModule', ['getPhotonModules']),

    /**
     * Dispatches the clearAdminErrors action
     *
     * @return  {void}
     */
    clearAdminErrors() {
      return store.dispatch(`${this.vuexModule}/clearAdminErrors`)
    },

    /**
     * Clears the errors from the UI
     *
     * @return  {void}
     */
    clearErrors() {
      this.serverError = null
    },

    /**
     * Bind evenBus listeners
     *
     * @return  {void}
     */
    initEventBusListener() {
      eventBus.$on('dropZoneQueueComplete', result => {
        if (this.admin.selectedModule.table_name == 'assets') {
          this.onSubmitSuccess(result)
        }
      })
    },

    /**
     * Returns the fields that should be rendered inline
     *
     * @return  {array}
     */
    inlineFieldTypes(field) {
      // If the entry form is rendered in sidebar none of the fields should be inline
      if (this.inSidebar) {
        return false
      }

      if (_.has(field, 'presentAsReference') && field.presentAsReference) {
        return !field.presentAsReference
      }

      if (_.has(field, 'inline')) {
        return field.inline
      }

      const inlineFieldTypes = photonConfig.inlineFieldTypes

      return inlineFieldTypes.indexOf(field.vueComponent) === -1 ? true : false
    },

    /**
     * Determines if field should be focused
     *
     * @param   {object}  field
     * @param   {integer}  i
     * @return  {Boolean}
     */
    isFocused(field, i) {
      if (this.admin.editorMode === 'create') {
        if (field.vueComponent === 'Section' && i === 0) {
          this.isSecondFieldFocused = true
          return false
        } else if (field.vueComponent !== 'Section' && i === 0) {
          return true
        } else if (i === 1 && this.isSecondFieldFocused) {
          return true
        }
        return false
      }
      return false
    },

    /**
     * Runs on successful submit
     *
     * @param   {object}  results
     * @return  {void}
     */
    onSubmitSuccess(result) {
      if (this.shouldRouterPush) {
        if (
          (this.admin.editorMode === 'edit' ||
            this.admin.editorMode === 'default') &&
          !this.admin.createAnother &&
          this.admin.selectedModule.table_name != 'assets'
        ) {
          router.push(
            `/admin/${this.admin.selectedModule.table_name}/${this.admin.entry.system.id}`
          )
        }
      }

      this.$emit('submitSuccess', {
        editorMode: this.admin.editorMode,
        id: _.get(this.admin, 'entry.system.id', null),
        result,
      })

      this.setSubmitEntryInProgress(false)

      if (!result) {
        return
      }

      // Refresh user meta data if editing own profile under users module
      if (
        this.admin.selectedModule.table_name === 'users' &&
        this.user.meta.id === result.entry.system.id
      ) {
        const apiToken = storage.get('apiToken')

        this.checkMe({ apiToken })
      }

      resetValidator(this.validator)

      this.clearErrors()

      if (this.admin.createAnother) {
        this.setCreateEntryUI().then(() => {
          this.toggleEntryUpdated()
        })
      }

      this.resetFormFields()

      if (
        this.admin.selectedModule.table_name === 'users' ||
        this.admin.selectedModule.table_name === 'roles'
      ) {
        this.getPhotonModules({ refreshList: true })
      }
    },

    /**
     * Dispatches the submitEntry action
     *
     * @return  {promise}
     */
    submitEntry() {
      if (
        this.admin.selectedModule.table_name === 'assets' &&
        this.admin.editorMode === 'create' &&
        this.vuexModule !== 'menuItemsEditor'
      ) {
        // handle the submission process to Dropzone
        eventBus.$emit('submitSelectedAssets', {})

        return Promise.resolve(false)
      }

      return store.dispatch(`${this.vuexModule}/submitEntry`)
    },

    /**
     * Dispatches the setSubmitEntryInProgress action
     *
     * @param   {boolean}  value
     * @return  {promise}
     */
    setSubmitEntryInProgress(value) {
      return store.dispatch(`${this.vuexModule}/setSubmitEntryInProgress`, {
        value,
      })
    },

    /**
     * Toggles the resetFormFields data property in order to force refresh of the FormFields component
     *
     * @param   {object}  options.includeFields
     * @return  {void}
     */
    resetFormFields({ includeFields = null } = {}) {
      this.formFieldsReset.resetData = moment().valueOf()

      this.formFieldsReset.includeFields = []

      if (includeFields) {
        this.formFieldsReset.includeFields = includeFields
      }
    },

    /**
     * Checks if a user has given role
     *
     * @param   {string}  role
     * @return  {bool}
     */
    userHasRole,
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      photonFields: 'photonField/photonField',
      user: 'user/user',
    }),

    /**
     * Create the admin getter based on the vuexModule prop
     *
     * @return  {object}
     */
    admin() {
      const getterName = `${this.vuexModule}/${this.vuexModule}`

      return this.$store.getters[getterName]
    },
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted() {
    this.$nextTick(() => {
      this.initEventBusListener()

      // Use uniform to style checkbox/radio elements
      $(this.$el)
        .find('.form-footer input[type="checkbox"]')
        .uniform()

      // Bootstrap validator for the form
      this.validator = bindValidatorToForm({
        selector: `#entry-editor-form-${this.vuexModule}`,

        onSubmit: _.debounce(() => {
          if (this.admin.editorMode === 'create') {
            eventBus.$emit('entryCreated')
          } else {
            eventBus.$emit('entryUpdated', this.vuexModule)
          }
          this.setSubmitEntryInProgress(true)

          // On submit clear any errors and submit entry changes
          this.clearAdminErrors()

          resetValidator(this.validator)

          this.submitEntry().then(result => {
            if (this.admin.isReference) {
              this.setSubmitEntryInProgress(false)

              this.$emit('referenceSubmitSuccess', result)

              return
            }

            // Don't run onSubmitSuccess() for assets module, as it will be ran async after being
            // triggered by a global event via eventBus
            if (this.admin.selectedModule.table_name == 'assets') {
              this.setSubmitEntryInProgress(false)

              return
            }

            this.onSubmitSuccess(result)
          })
        }, 250),
      })
    })
  },

  /**
   * Set the beforeDestroy hook
   *
   * @return  {void}
   */
  beforeDestroy() {
    if (this.validator) {
      destroyValidator(this.validator)
    }
  },

  /**
   * Set the watched properties
   *
   * @type  {Object}
   */
  watch: {
    /**
     * Resets the entry form
     *
     * @param   {bool}  newEntry
     * @param   {bool}  oldEntry
     * @return  {void}
     */
    formFieldsResetProp: {
      deep: true,
      handler(newEntry, oldEntry) {
        pWarn(
          'Watched property fired.',
          'EntryForm',
          'formFieldsResetProp',
          newEntry,
          oldEntry,
          this
        )

        $(this.$el)
          .find('.form-footer input[type="checkbox"]')
          .uniform()

        this.resetFormFields(newEntry)

        resetValidator(this.validator)

        this.clearErrors()
      },
    },

    /**
     * On entryFormError change
     *
     * @return  {void}
     */
    'admin.error': {
      deep: true,
      handler(newEntry, oldEntry) {
        pWarn(
          'Watched property fired.',
          'EntryForm',
          'formFieldsResetProp',
          newEntry,
          oldEntry,
          this
        )

        if (this.validator) {
          resetValidator(this.validator)
        }

        this.clearErrors()

        this.serverError = processErrors(this.validator, this.admin.error)

        const notificationMessage = this.admin.shouldForceDelete
          ? `<p>${this.serverError}</p><p>${i18n.t(
              'admin.relatedEntry'
            )}:  <a href="/cp/admin/${
              this.admin.deletionPreventedRelatedEntry.table
            }/${this.admin.deletionPreventedRelatedEntry.id}">${
              this.admin.deletionPreventedRelatedEntry.anchor_text
            }</a></p>`
          : this.serverError

        if (this.serverError) {
          const notification = {
            subject: i18n.t('errors.thereSBeenAProblem'),
            compiled_message: notificationMessage,
          }
          this.addNotification({ notification })
        }
      },
    },
  },
}
